"use client";

import { Button, cn } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { History, MapPin, RefreshCw, ScanBarcodeIcon } from "lucide-react";

export type ShopPosSalesHistoryToolbarProps = {
  shopId: string | undefined;
  branchTitle: string | null;
  total: number;
  offset: number;
  pageEnd: number;
  activeFilterCount: number;
  isRefreshing?: boolean;
  onRefresh?: () => void;
};

export function ShopPosSalesHistoryToolbar({
  shopId,
  branchTitle,
  total,
  offset,
  pageEnd,
  activeFilterCount,
  isRefreshing,
  onRefresh,
}: ShopPosSalesHistoryToolbarProps) {
  return (
    <header className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-border border-b bg-muted/25 px-4 py-3 sm:px-5 sm:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-bold font-heading text-foreground text-xl uppercase tracking-tight sm:text-2xl">
                ປະຫວັດບິນ POS
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 font-semibold text-[10px] text-sky-800 uppercase tracking-wide dark:text-sky-200">
                <History className="size-3" aria-hidden />
                ບິນຂາຍ
              </span>
              {total > 0 ? (
                <span className="rounded-full bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs tabular-nums">
                  {total} ບິນ
                </span>
              ) : null}
              {activeFilterCount > 0 ? (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 font-medium text-primary text-xs tabular-nums">
                  {activeFilterCount} ຕົວກອງ
                </span>
              ) : null}
            </div>

            <p className="mt-1.5 text-muted-foreground text-sm leading-snug">
              ຄົ້ນຫາ · ເບິ່ງລາຍລະອຽດ · void / refund ພາຍໃນ 48 ຊົ່ວໂມງ
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              {branchTitle != null && branchTitle !== "" ? (
                <span className="inline-flex items-center gap-1.5 text-sm">
                  <MapPin
                    className="size-3.5 shrink-0 text-primary"
                    aria-hidden
                  />
                  <span className="font-medium text-foreground">
                    {branchTitle}
                  </span>
                </span>
              ) : null}
              {total > 0 ? (
                <>
                  {branchTitle ? (
                    <span className="text-muted-foreground/50">·</span>
                  ) : null}
                  <span className="text-muted-foreground tabular-nums">
                    {offset + 1}–{pageEnd} ຈາກ {total}
                  </span>
                </>
              ) : null}
            </div>
          </div>

          {onRefresh ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-1.5 border-border px-3"
              disabled={isRefreshing}
              onClick={onRefresh}
              title="ໂຫຼດຄືນ"
            >
              <RefreshCw
                className={cn("size-4", isRefreshing && "animate-spin")}
                aria-hidden
              />
              <span className="hidden sm:inline">ໂຫຼດຄືນ</span>
            </Button>
          ) : null}
        </div>
      </div>

      {shopId ? (
        <div className="px-4 py-3 sm:px-5">
          <p className="mb-2 font-medium text-[10px] text-muted-foreground uppercase tracking-wider">
            ວຽກດ່ວນ
          </p>
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5 [scrollbar-width:thin]">
            <Link
              to="/app/shops/$shopId/pos"
              params={{ shopId }}
              className={cn(
                "inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 font-medium text-sm transition-colors touch-manipulation",
                "border-emerald-500/35 bg-emerald-500/10 text-emerald-950 hover:bg-emerald-500/15 dark:text-emerald-100",
              )}
            >
              <ScanBarcodeIcon className="size-4 shrink-0" aria-hidden />
              <span>ກັບໄປຂາຍ</span>
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  );
}
